import React from 'react';
import { FormControl, SelectV1, TextV2, InputV2 } from '@withjoy/joykit';
import { Toggle } from '@withjoy/joykit/components/SelectV1/styles';
import { SegmentedControl } from '@shared/components/ScheduleEditor/components/form/components/SegmentedControl/SegmentedControl';
import { useTranslation } from '@shared/core';

type SelectV1Response = {
  label: string;
  value: string;
} | null;

type EditViewProps = {
  forms: { id: string; title: string }[];
  currentFormId: string | null;
  displayMode: 'embedded' | 'link';
  formInvalid: boolean;
  onFormChange: (formId: string | null) => void;
  onDisplayModeChange: (mode: 'embedded' | 'link') => void;
  isOpen: boolean;
  handleMenuToggle: (value: SelectV1Response) => void;
  linkCTA: string;
  linkHeader: string;
  onCtaChange: (cta: string) => void;
  onHeaderChange: (header: string) => void;
};

export const EditView = ({
  forms,
  currentFormId,
  displayMode,
  formInvalid,
  onFormChange,
  onDisplayModeChange,
  isOpen,
  handleMenuToggle,
  linkCTA,
  linkHeader,
  onCtaChange,
  onHeaderChange,
}: EditViewProps) => {
  const defaultLabel = currentFormId ? forms.find(f => f.id === currentFormId)?.title || '' : '';
  const { tq } = useTranslation('formCustomComponent');

  const displayModeInfoText = () => {
    switch (displayMode) {
      case 'embedded':
        return tq`The form will be displayed directly on your website page. Visitors can fill out the form without leaving the page.`;
      case 'link':
        return tq`A link to the form will be displayed on your website page. Visitors will need to click the link to open and fill out the form.`;
      default:
        return '';
    }
  };

  return (
    <>
      <FormControl
        label={tq`Choose Form`}
        isRequired
        isInvalid={formInvalid}
        error={formInvalid ? tq`Please select a form.` : undefined}
        marginBottom="1.5rem"
      >
        <SelectV1
          options={forms.map(f => {
            return { label: f.title, value: f.id };
          })}
          defaultValue={{
            label: defaultLabel,
            value: currentFormId ?? '',
          }}
          autoComplete="off"
          searchable={false}
          onMenuOpen={() => handleMenuToggle(null)}
          onMenuClose={() => handleMenuToggle(null)}
          iconRight={
            <Toggle
              source="optionSelect"
              $rotate={!isOpen}
            />
          }
          onChange={value => onFormChange(value?.value || null)}
          zIndex={100}
        />
      </FormControl>
      <FormControl label={tq`Display Type`}>
        <>
          <SegmentedControl
            segments={[
              {
                label: tq`Embedded`,
              },
              {
                label: tq`Link`,
              },
            ]}
            selectedIndex={displayMode === 'embedded' ? 0 : 1}
            onSelect={(index, _) => onDisplayModeChange(index === 0 ? 'embedded' : 'link')}
            marginBottom="1rem"
          ></SegmentedControl>
          <TextV2
            typographyVariant="label2"
            marginBottom="1rem"
          >
            {displayModeInfoText()}
          </TextV2>
        </>
      </FormControl>
      {displayMode === 'link' && (
        <>
          <FormControl
            label={tq`Button Text`}
            marginBottom="1.5rem"
          >
            <InputV2
              value={linkCTA}
              onChange={e => onCtaChange(e.target.value)}
              placeholder={tq`Enter button text (e.g. 'Go To RSVP')`}
              width="100%"
            />
          </FormControl>
          <FormControl
            label={tq`Button Title`}
            marginBottom="1.5rem"
          >
            <InputV2
              value={linkHeader}
              onChange={e => onHeaderChange(e.target.value)}
              placeholder={tq`Enter heading text (e.g. 'Register for the Event')`}
              width="100%"
            />
          </FormControl>
        </>
      )}
    </>
  );
};
